import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatNotification } from '../utils/notificationDisplay';

const severityStyles = {
  DANGER: {
    iconWrap: 'bg-red-100 text-red-600',
    badge: 'bg-red-100 text-red-700',
    unread: 'bg-red-50/70',
  },
  WARNING: {
    iconWrap: 'bg-amber-100 text-amber-700',
    badge: 'bg-amber-100 text-amber-800',
    unread: 'bg-amber-50/60',
  },
  INFO: {
    iconWrap: 'bg-sky-100 text-sky-700',
    badge: 'bg-sky-100 text-sky-800',
    unread: 'bg-sky-50/70',
  },
};

function SeverityIcon({ type }) {
  if (type === 'DANGER' || type === 'WARNING') {
    return (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    );
  }
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

/**
 * Shared notification row for bell dropdown and full page.
 */
export default function NotificationItem({
  item,
  relativeTime,
  onOpen,
  compact = false,
}) {
  const { t } = useTranslation();
  const display = formatNotification(item, t);
  const styles = severityStyles[item.type] || severityStyles.INFO;
  const hasAction = Boolean(display.actionLabel);

  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className={`w-full text-left flex items-start gap-3 transition-colors hover:bg-slate-50
        ${compact ? 'px-4 py-3 border-b border-slate-100' : 'px-4 py-4'}
        ${!item.read ? styles.unread : ''}`}
    >
      <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${styles.iconWrap}`}>
        <SeverityIcon type={item.type} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className={`text-sm leading-snug ${!item.read ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
              {display.title}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className={`inline-flex rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${styles.badge}`}>
                {display.categoryLabel}
              </span>
              {!item.read && (
                <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-600">
                  {t('notifications.content.new')}
                </span>
              )}
            </div>
          </div>
          <span className="shrink-0 text-[11px] text-slate-400 whitespace-nowrap">
            {relativeTime}
          </span>
        </div>

        {display.message ? (
          <p className={`mt-1.5 text-xs leading-relaxed text-slate-500 ${compact ? 'line-clamp-2' : ''}`}>
            {display.message}
          </p>
        ) : null}

        {hasAction ? (
          <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary-600">
            {display.actionLabel}
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        ) : null}
      </div>

      {!item.read && (
        <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary-500" aria-hidden />
      )}
    </button>
  );
}
